import { representativesCarryingOf } from '@e-socna/folder-actors';

import type { ActorsCmp_ActorableRules } from '../fragments/actorable-rules.fragment';

import { representativeInheritancesOf } from './representative-inheritances.util';
import type { RuleList } from './actorable-rule-descriptions.util';
import type {
  InheritingOrganizationActor,
  OrganizationMemberships,
} from './representative-inheritances.util';

export type RepresentativeTypeRules = Pick<
  ActorsCmp_ActorableRules,
  | 'requiredQuotationEmitterRepresentativeTypeIds'
  | 'requiredQuotationReceiverRepresentativeTypeIds'
>;

export interface QuotationPartyReferences {
  readonly quotationEmitterCategoryId: string;
  readonly quotationReceiverCategoryId: string;
}

// The organization quotation party of a list (the emitter among the internal actors, the
// receiver among the external ones) and the representative types the workflow requires of it.
export interface QuotationParty {
  readonly categoryId: string;
  readonly requiredTypeIds: readonly string[];
  readonly label: string;
}

export type TypeActor = InheritingOrganizationActor &
  Readonly<{ categoryIds: readonly string[] }>;

export const quotationPartyOf = (
  list: RuleList,
  rules: RepresentativeTypeRules,
  references: QuotationPartyReferences,
): QuotationParty =>
  list === 'internal'
    ? {
        categoryId: references.quotationEmitterCategoryId,
        requiredTypeIds: rules.requiredQuotationEmitterRepresentativeTypeIds,
        label: "L'émetteur du devis",
      }
    : {
        categoryId: references.quotationReceiverCategoryId,
        requiredTypeIds: rules.requiredQuotationReceiverRepresentativeTypeIds,
        label: 'Le destinataire du devis (organisation)',
      };

// The required representative types that an organization quotation party of a list lacks.
export const missingRepresentativeTypeIds = ({
  actors,
  organizations,
  enabledStatusId,
  party,
}: Readonly<{
  actors: readonly TypeActor[];
  organizations: readonly OrganizationMemberships[];
  enabledStatusId: string;
  party: QuotationParty;
}>): readonly string[] => [
  ...new Set(
    actors
      .filter(
        ({ representatives, categoryIds }) =>
          !!representatives && categoryIds.includes(party.categoryId),
      )
      .flatMap((actor) => {
        const carried = new Set(
          representativeInheritancesOf(
            actor,
            organizations,
            enabledStatusId,
          ).flatMap(({ typeIds }) => typeIds),
        );

        return party.requiredTypeIds.filter((typeId) => !carried.has(typeId));
      }),
  ),
];

export const representativeTypesRefusalOf = (
  party: QuotationParty,
  missingTypeIds: readonly string[],
  labelOf: (typeId: string) => string,
): string | null =>
  missingTypeIds.length
    ? `${party.label} doit posséder ${representativesCarryingOf('type', missingTypeIds.map(labelOf))}.`
    : null;
